import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ApiError, api } from '../../api/client'
import { useList } from '../../api/queries'
import type { Equipment, InspectionDetail, Level, ScoredLevel, OperatingState, Rules, RuleCriterion, ScoreExplanation, Template, TemplateVersion } from '../../api/types'
import { useUser } from '../../auth/AuthContext'
import { Icon } from '../../components/Icon'
import { BandBadge, Card, ErrorState, Field, LoadingState, PageHeader, Segmented, cx } from '../../components/ui'
import { blankDraft, loadDraft, saveDraft, deleteDraft, setObservation, summarizeDraft, type InspectionDraft, type SheetReadings } from '../../features/field/draftStore'
import { syncDraft } from '../../features/field/sync'
import { BreakdownView } from '../../features/inspections/BreakdownView'
import { inspectionText, referenceBandLabel } from '../../features/inspections/presentation'
import { compressImage } from '../../lib/image'
import { formatScore } from '../../lib/bands'
import { humanize, levelLabel, todayIso } from '../../lib/format'
import { useOnline } from '../../lib/hooks'
import { scoreInspection, scoreMotorReference } from '../../lib/scoring'
import { HELPERS, helperRawValue, suggestionFor } from '../../lib/suggestions'

const LEVEL_OPTIONS: { value: Level; label: string; scored?: boolean }[] = [
  { value: 'normal', label: levelLabel('normal') },
  { value: 'medium', label: levelLabel('medium') },
  { value: 'critical', label: levelLabel('critical') },
  { value: 'not_applicable', label: levelLabel('not_applicable') },
  { value: 'not_measured', label: levelLabel('not_measured') },
]

function useTemplateRules() {
  return useQuery({
    queryKey: ['template-rules'],
    queryFn: async () => {
      const templates = await api.get<{ results: Template[] }>('/api/templates/')
      const t = templates.results.find((x) => x.code === 'MOTORREDUCER-13') ?? templates.results[0]
      const published = t.versions.filter((v) => v.status === 'published').sort((a, b) => b.number - a.number)[0]
      if (!published) throw new ApiError(404, 'no_template', 'No hay ninguna plantilla de inspección publicada.')
      const version = await api.get<TemplateVersion>(`/api/template-versions/${published.id}/`)
      return { versionId: published.id, rules: version.rules as unknown as Rules }
    },
    staleTime: 10 * 60_000,
  })
}

function CriterionCard({ c, draft, onChange }: { c: RuleCriterion; draft: InspectionDraft; onChange: (next: InspectionDraft) => void }) {
  const obs = draft.observations[c.code] ?? {}
  const helper = HELPERS[c.code]
  const answers = draft.helpers[c.code] ?? {}
  const title = `${c.number}. ${inspectionText(c.name)}`
  const suggestion = helper ? suggestionFor(helper.kind, answers) : null
  const setAnswer = (patch: Record<string, string>) => {
    const next = { ...answers, ...patch }
    onChange({ ...setObservation(draft, c.code, { rawValue: helper ? helperRawValue(helper.kind, next) : undefined }), helpers: { ...draft.helpers, [c.code]: next } })
  }
  return (
    <fieldset className="criterion" aria-label={undefined}>
      <div role="radiogroup" aria-label={title} className="criterion__group">
        <legend className="criterion__title"><span className="criterion__num">{c.number}</span> {inspectionText(c.name)}{obs.level === 'critical' ? <Icon name="triangle-alert" size={16} /> : null}</legend>
        <div className="criterion__options">
          {LEVEL_OPTIONS.map((o) => {
            const scored = o.value === 'normal' || o.value === 'medium' || o.value === 'critical'
            const sv = o.value as ScoredLevel
            return (
              <label key={o.value} className={cx('choice', obs.level === o.value && 'is-on', `choice--${o.value}`)}>
                <input type="radio" name={c.code} value={o.value} checked={obs.level === o.value} onChange={() => onChange(setObservation(draft, c.code, { level: o.value }))} />
                <span className="choice__name">{o.label}{scored ? <span className="mono choice__pts"> +{c.values[sv]}</span> : null}</span>
                {scored ? <span className="choice__text">{inspectionText(c.texts[sv])}</span> : null}
              </label>
            )
          })}
        </div>
      </div>
      {helper ? (
        <div role="group" aria-label={`${title}: ayuda`} className="helper">
          <p className="helper__q">{helper.question}</p>
          {helper.kind === 'seal' ? (
            <div className="row">
              <Field label="Reposición cada (días)" htmlFor={`${c.code}-days`}><input id={`${c.code}-days`} type="number" min="1" inputMode="numeric" value={answers.days ?? ''} onChange={(e) => setAnswer({ days: e.target.value, none: '' })} /></Field>
              <label className="check"><input type="checkbox" checked={answers.none === '1'} onChange={(e) => setAnswer({ none: e.target.checked ? '1' : '', days: '' })} /> No se necesita reposición</label>
            </div>
          ) : null}
          {helper.kind === 'litres' ? <Field label="Litros para llegar a la mitad del visor" htmlFor={`${c.code}-l`}><input id={`${c.code}-l`} type="number" min="0" step="0.1" inputMode="decimal" value={answers.litres ?? ''} onChange={(e) => setAnswer({ litres: e.target.value })} /></Field> : null}
          {helper.kind === 'temperature' ? <Field label="Temperatura (°C)" htmlFor={`${c.code}-t`}><input id={`${c.code}-t`} type="number" inputMode="decimal" value={answers.celsius ?? ''} onChange={(e) => setAnswer({ celsius: e.target.value })} /></Field> : null}
          {helper.kind === 'oil' ? (
            <div className="row">
              <Field label="Estado del aceite" htmlFor={`${c.code}-c`}>
                <select id={`${c.code}-c`} value={answers.condition ?? ''} onChange={(e) => setAnswer({ condition: e.target.value })}>
                  <option value="">Elegir…</option><option value="clean">Limpio, color normal</option><option value="dark">Oscuro pero viscoso</option><option value="thin">Perdió viscosidad</option>
                </select>
              </Field>
              <Field label="Horas de operación con este aceite" htmlFor={`${c.code}-h`} hint={answers.hours ? `Referencia de pérdida de viscosidad: ${Number(answers.hours) * 0.0005} h (0.0005 × horas)` : undefined}>
                <input id={`${c.code}-h`} type="number" min="0" inputMode="numeric" value={answers.hours ?? ''} onChange={(e) => setAnswer({ hours: e.target.value })} />
              </Field>
            </div>
          ) : null}
          {suggestion ? (
            <div className="helper__suggest">
              <span><strong>Sugerido: {levelLabel(suggestion.level)}.</strong> {suggestion.because}</span>
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => onChange(setObservation(draft, c.code, { level: suggestion.level }))}>Aplicar sugerencia</button>
            </div>
          ) : null}
        </div>
      ) : null}
      <Field label="Nota" htmlFor={`${c.code}-note`}><input id={`${c.code}-note`} value={obs.note ?? ''} onChange={(e) => onChange(setObservation(draft, c.code, { note: e.target.value }))} placeholder="Opcional" /></Field>
    </fieldset>
  )
}

function SheetCard({ draft, onChange }: { draft: InspectionDraft; onChange: (next: InspectionDraft) => void }) {
  const sheet = draft.sheet ?? {}
  const set = (patch: Partial<SheetReadings>) => onChange({ ...draft, sheet: { ...sheet, ...patch } })
  const num = (key: keyof SheetReadings, label: string, id: string, step = '0.1') => (
    <Field label={label} htmlFor={id}><input id={id} type="number" min="0" step={step} inputMode="decimal" value={sheet[key] ?? ''} onChange={(e) => set({ [key]: e.target.value })} /></Field>
  )
  return (
    <Card title="Mediciones y accesorios">
      <p className="muted">Datos informativos de la hoja del equipo: no puntúan ni cambian la banda.</p>
      <div className="form-stack">
        <Field label="Técnico revisor" htmlFor="technician-name"><input id="technician-name" maxLength={150} value={draft.technicianName ?? ''} onChange={(e) => onChange({ ...draft, technicianName: e.target.value })} /></Field>
        <Field label="Diagnóstico" htmlFor="diagnosis"><textarea id="diagnosis" value={draft.diagnosis ?? ''} onChange={(e) => onChange({ ...draft, diagnosis: e.target.value })} /></Field>
        <Field label="Recomendaciones" htmlFor="recommendations"><textarea id="recommendations" value={draft.recommendations ?? ''} onChange={(e) => onChange({ ...draft, recommendations: e.target.value })} /></Field>
        <Field label="Vibraciones" htmlFor="sheet-vibration">
          <select id="sheet-vibration" value={sheet.vibrationLevel ?? ''} onChange={(e) => set({ vibrationLevel: e.target.value })}>
            <option value="">Sin calificar</option><option value="normal">Normal</option><option value="medium">Medio</option><option value="critical">Crítico</option>
            <option value="not_applicable">No aplica</option><option value="not_measured">No medido</option>
          </select>
        </Field>
        {num('vibrationMmS', 'Vibración (mm/s)', 'sheet-vibration-mms')}
        {num('currentA', 'Corriente medida (A)', 'sheet-current')}
        {num('voltageV', 'Voltaje medido (V)', 'sheet-voltage')}
        {num('frequencyHz', 'Frecuencia medida (Hz)', 'sheet-frequency')}
        {num('motorTemperatureC', 'Temperatura del motor (°C)', 'sheet-motor-temp')}
        {num('reducerTemperatureC', 'Temperatura del reductor (°C)', 'sheet-reducer-temp')}
        <Field label="Accesorios" htmlFor="sheet-accessories"><input id="sheet-accessories" value={sheet.accessories ?? ''} onChange={(e) => set({ accessories: e.target.value })} placeholder="Opcional" /></Field>
      </div>
    </Card>
  )
}

function LivePreview({ rules, draft }: { rules: Rules; draft: InspectionDraft }) {
  const score = useMemo(() => scoreInspection(rules, Object.entries(draft.observations).filter(([, o]) => o.level).map(([criterion, o]) => ({ criterion, level: o.level as string })), draft.operatingState), [rules, draft.observations, draft.operatingState])
  return (
    <div role="status" aria-label="Puntaje en vivo" className="preview">
      <span className="preview__label">Puntaje en vivo</span>
      <span className="preview__total mono">{formatScore(score.total)}</span>
      <BandBadge band={score.band} size="sm" />
      <span className="muted">{score.scored_count} de {score.criteria_count} puntuados</span>
    </div>
  )
}

type Step = 'details' | 'criteria' | 'finish' | 'review'
const STEPS: { key: Step; label: string }[] = [{ key: 'details', label: 'Detalles' }, { key: 'criteria', label: 'Criterios' }, { key: 'finish', label: 'Fotos y notas' }, { key: 'review', label: 'Revisión' }]

export function InspectWizardPage() {
  const { code = '' } = useParams()
  const user = useUser()
  const online = useOnline()
  const eq = useList<Equipment>('/api/equipment/', { code })
  const tpl = useTemplateRules()
  const [draft, setDraftState] = useState<InspectionDraft | null>(null)
  const [step, setStep] = useState<Step>('details')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<{ inspection: InspectionDetail; score: ScoreExplanation } | null>(null)
  const [resumed, setResumed] = useState(false)
  const dirty = useRef(false)
  const equipment = eq.data?.results[0]

  useEffect(() => {
    if (!equipment || draft) return
    void loadDraft(user.id, code).then((saved) => {
      if (saved && saved.status !== 'synced') { setDraftState(saved); setResumed(true) } else setDraftState(blankDraft({ userId: user.id, equipmentId: equipment.id, equipmentCode: code, inspectedOn: todayIso() }))
    })
  }, [equipment, draft, user.id, code])

  useEffect(() => {
    if (draft && tpl.data && !draft.rules) setDraftState({ ...draft, rules: tpl.data.rules, templateVersionId: tpl.data.versionId })
  }, [draft, tpl.data])

  useEffect(() => {
    if (!draft || !dirty.current) return
    const t = setTimeout(() => void saveDraft(draft), 400)
    return () => clearTimeout(t)
  }, [draft])

  const change = useCallback((next: InspectionDraft) => { dirty.current = true; setDraftState(next) }, [])

  const rules = draft?.rules ?? tpl.data?.rules
  if (eq.isPending || !draft || (!rules && tpl.isPending)) return eq.isError ? <ErrorState error={eq.error} /> : <LoadingState label="Preparando la inspección…" />
  if (!equipment) return <ErrorState error={new ApiError(404, 'not_found', `Ningún equipo tiene el código ${code}.`)} />
  if (!rules) return <ErrorState error={tpl.error} onRetry={() => void tpl.refetch()} title="No se pudo cargar la plantilla de inspección" />

  const summary = summarizeDraft(draft)
  const index = STEPS.findIndex((s) => s.key === step)
  const motorCodes = rules.motor?.criteria ?? []

  async function addPhotos(files: FileList | null, criterion?: string) {
    if (!files) return
    const photos = [...draft!.photos]
    for (const f of Array.from(files)) {
      try {
        const small = await compressImage(f)
        photos.push({ id: crypto.randomUUID(), name: small.name, type: small.type, data: await small.arrayBuffer(), criterion, uploaded: false })
      } catch (e) { setError(e instanceof Error ? e.message : 'No se pudo agregar la foto.') }
    }
    change({ ...draft!, photos })
  }

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      const res = await syncDraft(draft!, { api, save: async (d) => { const s = await saveDraft(d); setDraftState(s); return s } })
      await deleteDraft(user.id, code)
      setResult(res)
    } catch (e) {
      setError(`Guardado en este dispositivo. ${e instanceof ApiError && e.status !== 0 ? e.message : 'No se pudo conectar con el servidor'} — enviar de nuevo cuando vuelva la conexión.`)
    } finally { setBusy(false) }
  }

  if (result) {
    return (
      <div className="page page--narrow">
        <PageHeader title="Inspección enviada" subtitle={<>{equipment.name} · <span className="mono">{code}</span></>} />
        <Card title="Puntaje congelado"><BreakdownView score={result.score} /></Card>
        <div className="row"><Link className="btn btn--primary" to={`/inspections/${result.inspection.id}`}>Abrir inspección</Link><Link className="btn btn--ghost" to={`/e/${code}`}>Volver al equipo</Link></div>
      </div>
    )
  }

  const motorScore = scoreMotorReference(rules, motorCodes.filter((c) => draft.observations[c]?.motorLevel).map((c) => ({ criterion: c, level: draft.observations[c].motorLevel as string })), draft.operatingState)

  return (
    <div className="page page--narrow wizard">
      <PageHeader title="Nueva inspección" subtitle={<>{equipment.name} · <span className="mono">{code}</span></>} />
      {resumed ? <p role="note" className="callout">Se reanudó un borrador guardado en este dispositivo ({summary.answered} respondidos, {summary.photos} fotos).</p> : null}
      {!online ? <p role="note" className="callout callout--warn"><Icon name="wifi-off" size={14} /> Sin conexión. Las respuestas se guardan en este dispositivo y se envían al enviar la inspección.</p> : null}
      <ol className="steps" aria-label="Pasos">{STEPS.map((s, i) => <li key={s.key} aria-current={s.key === step ? 'step' : undefined} className={cx(i < index && 'is-done')}>{s.label}</li>)}</ol>
      <LivePreview rules={rules} draft={draft} />

      {step === 'details' ? (
        <Card title="Detalles">
          <div className="form-stack">
            <Field label="Fecha de inspección" htmlFor="insp-date"><input id="insp-date" type="date" max={todayIso()} value={draft.inspectedOn} onChange={(e) => change({ ...draft, inspectedOn: e.target.value })} /></Field>
            <Segmented label="Estado de operación" value={draft.operatingState === 'unknown' ? 'running' : draft.operatingState} onChange={(v) => change({ ...draft, operatingState: v as OperatingState })} options={[{ value: 'running', label: 'En marcha' }, { value: 'off', label: 'Apagado' }]} />
            {draft.operatingState === 'off' ? <p className="callout">El equipo está apagado: solo los criterios estáticos son significativos y el resultado se etiqueta como tal.</p> : null}
          </div>
        </Card>
      ) : null}

      {step === 'criteria' ? (
        <>
          {rules.criteria.map((c) => <CriterionCard key={c.code} c={c} draft={draft} onChange={change} />)}
          {motorCodes.length ? (
            <Card title={<>Diagnóstico del motor <span className="tag-reference">Referencia</span></>}>
              <p className="muted">{inspectionText(rules.motor?.note ?? 'Solo informativo; nunca cambia la banda oficial.')}</p>
              {motorCodes.map((code) => {
                const c = rules.criteria.find((x) => x.code === code)!
                return (
                  <Field key={code} label={`${c.number}. ${inspectionText(c.name)} (motor)`} htmlFor={`motor-${code}`}>
                    <select id={`motor-${code}`} value={draft.observations[code]?.motorLevel ?? ''} onChange={(e) => change(setObservation(draft, code, { motorLevel: e.target.value || null }))}>
                      <option value="">Sin calificar</option><option value="normal">Normal</option><option value="medium">Medio</option><option value="critical">Crítico</option>
                    </select>
                  </Field>
                )
              })}
              {motorScore?.total ? <p>Total de referencia {motorScore.total} ({referenceBandLabel(motorScore.band)}).</p> : null}
            </Card>
          ) : null}
          <SheetCard draft={draft} onChange={change} />
        </>
      ) : null}

      {step === 'finish' ? (
        <Card title="Fotos y notas">
          <div className="form-stack">
            <Field label="Agregar fotos" htmlFor="photos" hint="Las fotos se comprimen en este dispositivo antes de cargarlas (jpg, png o webp).">
              <input id="photos" type="file" accept="image/jpeg,image/png,image/webp" capture="environment" multiple onChange={(e) => void addPhotos(e.target.files)} />
            </Field>
            <ul className="plain">{draft.photos.map((p) => <li key={p.id}>{p.name} <button type="button" className="btn btn--ghost btn--sm" onClick={() => change({ ...draft, photos: draft.photos.filter((x) => x.id !== p.id) })}>Quitar</button></li>)}</ul>
            <Field label="Notas de toda la inspección" htmlFor="notes"><textarea id="notes" rows={4} value={draft.notes} onChange={(e) => change({ ...draft, notes: e.target.value })} /></Field>
          </div>
        </Card>
      ) : null}

      {step === 'review' ? (
        <Card title="Revisión">
          <dl className="defs"><dt>Fecha</dt><dd>{draft.inspectedOn}</dd><dt>Estado</dt><dd>{inspectionText(humanize(draft.operatingState))}</dd><dt>Respondidos</dt><dd>{summary.answered} de {rules.criteria.length} criterios</dd><dt>Fotos</dt><dd>{summary.photos}</dd></dl>
          {summary.answered < rules.criteria.length ? <p className="callout">Los criterios sin responder se guardan como faltantes, nunca como 0, y reducen la cobertura.</p> : null}
          <p className="muted">Al enviar, la inspección queda congelada: una corrección posterior es una nueva inspección.</p>
        </Card>
      ) : null}

      {error ? <p role="alert" className="form-error"><Icon name="triangle-alert" size={16} /> {error}</p> : null}
      <div className="wizard__nav">
        <button type="button" className="btn btn--ghost" disabled={index === 0} onClick={() => setStep(STEPS[index - 1].key)}>Atrás</button>
        {step !== 'review' ? <button type="button" className="btn btn--primary" onClick={() => setStep(STEPS[index + 1].key)}>Siguiente</button>
          : <button type="button" className="btn btn--primary" disabled={busy || summary.answered === 0} onClick={() => void submit()}>{busy ? 'Enviando…' : 'Enviar inspección'}</button>}
      </div>
    </div>
  )
}
